import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { storeCommissionApi } from '../services/api';
import { formatPrice } from '../services/currencyUtils';
import CommissionScheduleEditor, {
  draftFromSchedule,
  draftToPayload,
  validateDraft,
  describeSchedule,
} from './CommissionScheduleEditor';

/** Whole amounts without decimals, otherwise 2 — so a ₪0.40 gap never reads as ₪0. */
const money = (amount, currencyCode) =>
  formatPrice(amount, currencyCode, Number.isInteger(Number(amount)) ? 0 : 2);

/**
 * This month's rate (from last month's sales), next month's rate when this month's sales already
 * change it, and what is left to sell this month to reach the tier above that.
 */
const Preview = ({ preview }) => {
  if (!preview) return null;
  const tiered = preview.mode === 'TIERED';
  const nextMonthRate = tiered ? preview.nextMonthRatePercent : null;
  const nextMonthDiffers = nextMonthRate != null && Number(nextMonthRate) !== Number(preview.ratePercent);
  return (
    <Box sx={{ mt: 2 }} data-testid="commission-preview">
      <Typography variant="subtitle2">Rate this store pays this month</Typography>
      <Typography variant="h5" component="p">{preview.ratePercent}%</Typography>
      <Typography variant="body2" color="text.secondary">
        {preview.source === 'CUSTOM' ? 'Custom schedule' : 'Platform default'}
        {tiered && preview.currencyCode && (
          ` · last month ${money(preview.lastMonthVolume, preview.currencyCode)}`
          + ` · this month so far ${money(preview.thisMonthVolume, preview.currencyCode)}`
        )}
      </Typography>
      {nextMonthDiffers && (
        <Typography variant="body2" color="text.secondary">
          Next month at this month&apos;s sales so far: {nextMonthRate}%
        </Typography>
      )}
      {tiered && preview.nextTier && (
        <Typography variant="body2" color="text.secondary">
          {money(preview.nextTier.remainingThisMonth, preview.currencyCode)} more this month
          (from {money(preview.nextTier.threshold, preview.currencyCode)})
          brings next month to {preview.nextTier.ratePercent}%
        </Typography>
      )}
    </Box>
  );
};

/** Per-store commission section for the admin provider page. Render for STORE providers only. */
const StoreCommissionSection = ({ providerId }) => {
  const [data, setData] = useState(null);
  const [choice, setChoice] = useState('DEFAULT');
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');

  const apply = useCallback((res) => {
    setData(res);
    setChoice(res.usesDefault ? 'DEFAULT' : 'CUSTOM');
    setDraft(draftFromSchedule(res.schedule));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      apply(await storeCommissionApi.getForProvider(providerId));
    } catch (err) {
      setLoadError(err.backendMessage || err.message || 'Failed to load the commission schedule');
    } finally {
      setLoading(false);
    }
  }, [providerId, apply]);

  useEffect(() => { load(); }, [load]);

  const run = async (action, doneMessage) => {
    setBusy(true);
    setActionError('');
    setSuccess('');
    try {
      apply(await action());
      setSuccess(doneMessage);
    } catch (err) {
      setActionError(err.backendMessage || err.message || 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const saveCustom = () => run(
    () => storeCommissionApi.updateForProvider(providerId, draftToPayload(draft)),
    'Custom commission saved',
  );
  const revert = () => run(
    () => storeCommissionApi.revertForProvider(providerId),
    'Reverted to the default commission',
  );

  const valid = draft ? validateDraft(draft, { flatRequired: false }).valid : false;

  const onChoice = (next) => {
    setChoice(next);
    setSuccess('');
    // Starting a custom schedule from the one in force is friendlier than a blank form.
    if (next === 'CUSTOM' && data?.usesDefault) setDraft(draftFromSchedule(data.schedule));
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }} data-testid="store-commission-section">
      <Typography variant="h5" component="h2" gutterBottom>Store commission</Typography>

      {loading && <CircularProgress size={24} aria-label="Loading commission schedule" />}

      {!loading && loadError && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {loadError}
        </Alert>
      )}

      {!loading && data && draft && (
        <>
          <FormControl>
            <RadioGroup value={choice} onChange={(e) => onChoice(e.target.value)}>
              <FormControlLabel
                value="DEFAULT"
                control={<Radio disabled={busy} />}
                label={`Use default (currently: ${describeSchedule(data.defaultSchedule)})`}
              />
              <FormControlLabel
                value="CUSTOM"
                control={<Radio disabled={busy} />}
                label="Custom"
              />
            </RadioGroup>
          </FormControl>

          {choice === 'CUSTOM' && (
            <Box sx={{ mt: 2 }}>
              <CommissionScheduleEditor value={draft} onChange={setDraft} disabled={busy} />
            </Box>
          )}

          {choice === 'DEFAULT' && !data.usesDefault && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This store currently has a custom schedule. Use &quot;Revert to default&quot; to remove it.
            </Alert>
          )}

          {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {choice === 'CUSTOM' && (
              <Button variant="contained" onClick={saveCustom} disabled={!valid || busy}>
                Save custom schedule
              </Button>
            )}
            {!data.usesDefault && (
              <Button variant="outlined" color="warning" onClick={revert} disabled={busy}>
                Revert to default
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />
          <Preview preview={data.preview} />
        </>
      )}
    </Paper>
  );
};

export default StoreCommissionSection;
